import { Check } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";
import { LogoMark } from "./Logo";
import { SITE } from "@/lib/site";
import { useSiteImages } from "@/context/SiteImagesContext";

const focus = ["Professional design", "Mobile responsiveness", "Modern technology", "Clear communication", "Custom solutions", "We launch it and take care of it"];

export const About = () => {
  const { src } = useSiteImages();
  const aboutImage = src("about_image");
  return (
  <section id="about" className="section" aria-labelledby="about-title">
    <div className="container-x grid gap-12 lg:grid-cols-2 lg:gap-20 items-center">
      <div>
        <SectionHeading id="about-title" eyebrow={`About ${SITE.name}`} title="We create modern websites that help businesses establish a stronger online presence." />
        <Reveal delay={0.15}>
          <p className="mt-6 text-base md:text-lg text-slate-400 leading-relaxed">
            {SITE.name} is an Australian website design and development business. Every website is designed around the business it represents — its customers, its goals and how it works day to day — using modern technology and a clear, straightforward process. We launch your new website and take care of it as well, so you're never left managing it on your own.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <ul className="mt-8 grid gap-3 sm:grid-cols-2">
            {focus.map((f) => (
              <li key={f} className="flex items-center gap-3 text-sm sm:text-base text-slate-200">
                <span className="grid h-6 w-6 place-items-center rounded-full bg-gradient-to-br from-indigo-500/30 to-violet-500/20 border border-white/10 text-indigo-200"><Check className="h-3.5 w-3.5" aria-hidden="true" /></span>
                {f}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>

      <Reveal delay={0.1}>
        {aboutImage && (
          <img src={aboutImage} alt={`${SITE.name} – about us`} className="mb-6 aspect-video w-full rounded-3xl border border-white/10 object-cover" loading="lazy" data-testid="about-custom-image" />
        )}
        <div className="relative rounded-3xl p-[1px] bg-gradient-to-br from-indigo-400/40 via-transparent to-violet-500/40">
          <div className="card-dark p-8 sm:p-10 overflow-hidden" data-testid="about-business-card">
            <div className="orb -right-16 -top-16 h-48 w-48 bg-violet-500/25" aria-hidden="true" />
            <LogoMark className="h-16 w-16 shadow-[0_0_40px_-8px_rgba(99,102,241,0.8)]" />
            <h3 className="mt-7 font-display text-2xl font-semibold text-white">{SITE.name}</h3>
            <p className="mt-1 text-sm text-slate-400">{SITE.tagline}</p>
            <dl className="mt-8 space-y-4 border-t border-white/[0.08] pt-6 text-sm">
              <div className="flex justify-between gap-4"><dt className="text-slate-500">ABN</dt><dd className="font-mono text-slate-200" data-testid="about-abn">{SITE.abn}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-slate-500">Phone</dt><dd><a className="font-mono text-slate-200 hover:text-white" href={SITE.phoneHref} data-testid="about-phone">{SITE.phone}</a></dd></div>
              <div className="flex justify-between gap-4"><dt className="text-slate-500">Email</dt><dd><a className="text-slate-200 hover:text-white break-all" href={SITE.emailHref} data-testid="about-email">{SITE.email}</a></dd></div>
              <div className="flex justify-between gap-4"><dt className="text-slate-500">Based in</dt><dd className="text-slate-200">Australia</dd></div>
            </dl>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
  );
};
